import { type Modification, type ModificationsMap } from '../../types';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Removes a modification from the list of active modifications.
 *
 * When `shouldRemoveAllPortions === false`:
 *    If the modification for the provided ingredientId already exists in the list,
 *    it decreases its quantity. Otherwise, it removes the modification.
 * When `shouldRemoveAllPortions === true`:
 *    All portions of the modification will be removed.
 */
export function removeModificationToActiveModificationsList(params: Params) {
  const {
    ingredientId,
    activeModifications,
    allModificationsMap,
    shouldRemoveAllPortions,
  } = params;

  const modification = allModificationsMap.get(ingredientId);

  if (!modification) {
    return activeModifications;
  }

  const checkIfHasModification = checkIfMatchesIngredient(ingredientId);
  const checkIfNotMatchingIngredient = checkIfNotMatchingIngredientFactory(ingredientId); // prettier-ignore
  const updateModificationQuantity = updateModificationQuantityFactory(ingredientId); // prettier-ignore

  // ─── Remove all portions ─────────────────────────────────────────────

  if (shouldRemoveAllPortions) {
    return activeModifications.filter(checkIfNotMatchingIngredient);
  }

  // ─── Remove a single portion ─────────────────────────────────────────

  const hasModification = activeModifications.some(checkIfHasModification);

  if (hasModification) {
    return activeModifications.map(updateModificationQuantity);
  }

  return activeModifications.filter(checkIfNotMatchingIngredient);
}

// ─────────────────────────────────────────────────────────────────────────────

function checkIfNotMatchingIngredientFactory(ingredientId: string) {
  return function (modification: Modification) {
    return modification.ingredient?.id !== ingredientId;
  };
}

function checkIfMatchesIngredient(ingredientId: string) {
  return function (modification: Modification) {
    const { ingredient, quantity } = modification;

    return ingredient?.id === ingredientId && quantity > 1;
  };
}

function updateModificationQuantityFactory(ingredientId: string) {
  return function (modification: Modification) {
    const isTargetModification = modification.ingredient?.id === ingredientId;

    return isTargetModification
      ? withDecreasedQuantity(modification)
      : modification;
  };
}

function withDecreasedQuantity(modification: Modification): Modification {
  return { ...modification, quantity: modification.quantity - 1 };
}

// ─── Types ───────────────────────────────────────────────────────────────────

type Params = {
  ingredientId: string;
  activeModifications: Modification[];
  allModificationsMap: ModificationsMap;
  shouldRemoveAllPortions?: boolean;
};
